const WIDTH = 1200;
const HEIGHT = 1500;

export const SHARE_GALLERY_THEMES = [
  { id: "plain", label: "plain tiles" },
  { id: "fridge", label: "fridge magnets" },
  { id: "newspaper", label: "newspaper" },
  { id: "ink", label: "ink wash" },
  { id: "festival", label: "festival poster" }
];

const THEME_IDS = new Set(SHARE_GALLERY_THEMES.map((theme) => theme.id));

export function buildShareGallerySvg(completion, requestedTheme = "plain", displayOptions = {}) {
  const theme = THEME_IDS.has(requestedTheme) ? requestedTheme : "plain";
  const options = {
    showTitle: theme === "newspaper" || displayOptions.showTitle !== false,
    showScore: displayOptions.showScore !== false
  };
  const rows = normalizeRows(completion?.rows);
  const date = formatGalleryDate(completion?.date);
  const difficulty = String(completion?.difficulty || "game").toLowerCase();
  const score = Number(completion?.score || 0);
  const title = `totonoeyo result, ${date}, ${difficulty}, score ${score}`;
  const content = themeContent(theme, rows, score, options);

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" width="${WIDTH}" height="${HEIGHT}" role="img" aria-label="${escapeXml(title)}">
    <title>${escapeXml(title)}</title>
    <defs>
      <filter id="softShadow" x="-20%" y="-20%" width="150%" height="160%">
        <feDropShadow dx="8" dy="11" stdDeviation="5" flood-color="#1b1718" flood-opacity="0.22"/>
      </filter>
      <filter id="paperGrain" x="0" y="0" width="100%" height="100%">
        <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="2" seed="11" result="noise"/>
        <feBlend in="SourceGraphic" in2="noise" mode="multiply"/>
      </filter>
      <pattern id="newsprint" width="8" height="8" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r="0.8" fill="#1b1718" opacity="0.08"/>
      </pattern>
    </defs>
    ${content}
    ${footer(date, difficulty, theme)}
  </svg>`;
}

export function shareGalleryFilename(completion, theme) {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(completion?.date || "") ? completion.date : "result";
  const difficulty = String(completion?.difficulty || "game").replace(/[^a-z0-9-]/gi, "").toLowerCase();
  const style = THEME_IDS.has(theme) ? theme : "plain";
  return `totonoeyo-${date}-${difficulty}-${style}.png`;
}

function normalizeRows(value) {
  const rows = Array.isArray(value) ? value.slice(0, 5) : [];
  while (rows.length < 5) rows.push([]);
  return rows.map((row) => (
    Array.isArray(row)
      ? row.map((letter) => String(letter || "").slice(0, 1).toUpperCase()).filter(Boolean)
      : []
  ));
}

function formatGalleryDate(dateKey) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey || "")) return "daily game";
  const date = new Date(`${dateKey}T00:00:00Z`);
  const day = date.getUTCDate();
  const monthAndYear = date.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC"
  });
  return `${day}${ordinalSuffix(day)} ${monthAndYear}`;
}

function themeContent(theme, rows, score, options) {
  if (theme === "fridge") return fridgeTheme(rows, score, options);
  if (theme === "newspaper") return newspaperTheme(rows, score, options);
  if (theme === "ink") return inkTheme(rows, score, options);
  if (theme === "festival") return festivalTheme(rows, score, options);
  return plainTheme(rows, score, options);
}

function plainTheme(rows, score, options) {
  return `
    <rect width="1200" height="1500" fill="#feefe5"/>
    <path d="M80 94H1120" stroke="#bc002d" stroke-width="10"/>
    ${options.showTitle ? '<text x="80" y="205" fill="#1b1718" font-family="IBM Plex Serif, Georgia, serif" font-size="74" font-weight="300">totonoeyo</text>' : ""}
    ${options.showScore ? `<text x="1120" y="205" fill="#bc002d" font-family="IBM Plex Mono, monospace" font-size="76" font-weight="300" text-anchor="end">${score}</text>` : ""}
    ${tileRows(rows, {
      startY: 365,
      tileFill: "#ffffff",
      tileStroke: "#8f8587",
      letterFill: "#1b1718",
      accent: "#bc002d",
      emptyStroke: "#cfc7c5"
    })}`;
}

function fridgeTheme(rows, score, options) {
  const colours = ["#bc002d", "#00916e", "#ffcf00", "#7d5ba6", "#feefe5"];
  return `
    <rect width="1200" height="1500" fill="#d7f1e9"/>
    <rect x="48" y="48" width="1104" height="1404" fill="#edf0ee" stroke="#a9b0ad" stroke-width="8" rx="24"/>
    <path d="M1035 80V1420" stroke="#c9cfcc" stroke-width="6"/>
    ${options.showTitle ? '<text x="96" y="196" fill="#1b1718" font-family="IBM Plex Serif, Georgia, serif" font-size="72" font-weight="300">totonoeyo</text>' : ""}
    ${options.showScore ? `<circle cx="1006" cy="168" r="82" fill="#bc002d"/>
    <text x="1006" y="193" fill="#ffffff" font-family="IBM Plex Mono, monospace" font-size="66" font-weight="400" text-anchor="middle">${score}</text>` : ""}
    ${magnetRows(rows, colours)}`;
}

function newspaperTheme(rows, score, options) {
  const lines = newspaperHeadlineLines(rows);
  const longestLine = Math.max(...lines.map((line) => line.length), 1);
  const headlineSize = Math.max(70, Math.min(116, 1010 / (longestLine * 0.58)));
  const headlineMarkup = lines.map((line, index) => (
    `<text x="600" y="${275 + index * 130}" fill="#1b1718" font-family="IBM Plex Serif, Georgia, serif" font-size="${round(headlineSize)}" font-weight="600" text-anchor="middle">${escapeXml(line)}</text>`
  )).join("");
  const columns = newspaperColumns();
  return `
    <rect width="1200" height="1500" fill="#fbf7eb"/>
    <rect width="1200" height="1500" fill="url(#newsprint)"/>
    <text x="70" y="112" fill="#1b1718" font-family="IBM Plex Serif, Georgia, serif" font-size="72" font-weight="600">TOTONOEYO TIMES</text>
    ${options.showScore ? `<text x="1130" y="112" fill="#1b1718" font-family="IBM Plex Serif, Georgia, serif" font-size="68" font-weight="600" text-anchor="end">${score}</text>` : ""}
    <path d="M70 144H1130M70 160H1130" stroke="#1b1718" stroke-width="4"/>
    ${headlineMarkup}
    <path d="M70 465H1130" stroke="#1b1718" stroke-width="5"/>
    <path d="M70 481H1130" stroke="#8f8587" stroke-width="2"/>
    ${columns}`;
}

function inkTheme(rows, score, options) {
  return `
    <rect width="1200" height="1500" fill="#f7f4f2"/>
    <circle cx="990" cy="240" r="160" fill="none" stroke="#cfc7c5" stroke-width="42" stroke-dasharray="650 230" transform="rotate(-28 990 240)"/>
    <path d="M92 116C300 82 520 132 742 102" fill="none" stroke="#1b1718" stroke-width="18" stroke-linecap="round" opacity="0.9"/>
    ${options.showTitle ? '<text x="92" y="232" fill="#1b1718" font-family="IBM Plex Serif, Georgia, serif" font-size="80" font-weight="300">totonoeyo</text>' : ""}
    ${options.showScore ? `<rect x="945" y="160" width="150" height="150" fill="#bc002d" transform="rotate(3 1020 235)"/>
    <text x="1020" y="260" fill="#ffffff" font-family="IBM Plex Mono, monospace" font-size="70" text-anchor="middle">${score}</text>` : ""}
    ${tileRows(rows, {
      startY: 400,
      tileFill: "#1b1718",
      tileStroke: "#1b1718",
      letterFill: "#ffffff",
      accent: "#bc002d",
      emptyStroke: "#cfc7c5",
      gap: 6
    })}`;
}

function festivalTheme(rows, score, options) {
  return `
    <rect width="1200" height="1500" fill="#bc002d"/>
    <path d="M0 100H1200M0 1400H1200" stroke="#ffcf00" stroke-width="18"/>
    <circle cx="105" cy="185" r="24" fill="#ffcf00"/><circle cx="1095" cy="185" r="24" fill="#ffcf00"/>
    ${options.showTitle ? '<text x="600" y="220" fill="#feefe5" font-family="IBM Plex Serif, Georgia, serif" font-size="90" font-weight="300" text-anchor="middle">totonoeyo</text>' : ""}
    ${options.showScore ? `<text x="600" y="294" fill="#ffcf00" font-family="IBM Plex Mono, monospace" font-size="54" font-weight="300" text-anchor="middle">score ${score}</text>` : ""}
    ${tileRows(rows, {
      startY: 405,
      tileFill: "#feefe5",
      tileStroke: "#ffcf00",
      letterFill: "#1b1718",
      accent: "#00916e",
      emptyStroke: "#950024",
      gap: 10
    })}`;
}

function tileRows(rows, options) {
  const gap = options.gap ?? 12;
  const longestRow = Math.max(...rows.map((row) => row.length), 1);
  const tileSize = Math.min(132, (960 - gap * Math.max(0, longestRow - 1)) / longestRow);
  return rows.map((row, rowIndex) => {
    if (!row.length) {
      const y = options.startY + rowIndex * 176;
      return `<path d="M390 ${y + 66}H810" stroke="${options.emptyStroke}" stroke-width="3" stroke-dasharray="10 12"/>`;
    }
    const totalWidth = row.length * tileSize + Math.max(0, row.length - 1) * gap;
    const startX = (WIDTH - totalWidth) / 2;
    const y = options.startY + rowIndex * 176;
    return row.map((letter, index) => {
      const x = startX + index * (tileSize + gap);
      const fontSize = Math.max(38, tileSize * 0.5);
      return `<g>
        <rect x="${round(x)}" y="${y}" width="${round(tileSize)}" height="${round(tileSize)}" fill="${options.tileFill}" stroke="${options.tileStroke}" stroke-width="4"/>
        <rect x="${round(x)}" y="${round(y + tileSize - 10)}" width="${round(tileSize)}" height="10" fill="${options.accent}"/>
        <text x="${round(x + tileSize / 2)}" y="${round(y + tileSize * 0.68)}" fill="${options.letterFill}" font-family="IBM Plex Sans, Arial, sans-serif" font-size="${round(fontSize)}" font-weight="600" text-anchor="middle">${escapeXml(letter)}</text>
      </g>`;
    }).join("");
  }).join("");
}

function magnetRows(rows, colours) {
  const gap = 18;
  const longestRow = Math.max(...rows.map((row) => row.length), 1);
  const tileSize = Math.min(126, (900 - gap * Math.max(0, longestRow - 1)) / longestRow);
  return rows.map((row, rowIndex) => {
    if (!row.length) return "";
    const totalWidth = row.length * tileSize + Math.max(0, row.length - 1) * gap;
    const startX = 95 + (940 - totalWidth) / 2;
    const y = 360 + rowIndex * 184;
    return row.map((letter, index) => {
      const x = startX + index * (tileSize + gap);
      const rotation = ((rowIndex * 7 + index * 5) % 9) - 4;
      const fill = colours[(rowIndex + index) % colours.length];
      const ink = fill === "#ffcf00" || fill === "#feefe5" ? "#1b1718" : "#ffffff";
      return `<g transform="rotate(${rotation} ${round(x + tileSize / 2)} ${round(y + tileSize / 2)})" filter="url(#softShadow)">
        <rect x="${round(x)}" y="${y}" width="${round(tileSize)}" height="${round(tileSize)}" rx="10" fill="${fill}"/>
        <text x="${round(x + tileSize / 2)}" y="${round(y + tileSize * 0.69)}" fill="${ink}" font-family="IBM Plex Sans, Arial, sans-serif" font-size="${round(tileSize * 0.52)}" font-weight="600" text-anchor="middle">${escapeXml(letter)}</text>
      </g>`;
    }).join("");
  }).join("");
}

function footer(date, difficulty, theme) {
  const light = theme === "festival";
  const fill = light ? "#feefe5" : "#5f5759";
  const separator = light ? "#ffcf00" : "#cfc7c5";
  return `<g>
    <path d="M80 1378H1120" stroke="${separator}" stroke-width="3"/>
    <text x="600" y="1435" fill="${fill}" font-family="IBM Plex Serif, Georgia, serif" font-size="28" font-weight="300" text-anchor="middle">totonoeyo - ${escapeXml(difficulty)} - ${escapeXml(date)}</text>
  </g>`;
}

function newspaperHeadlineLines(rows) {
  const words = rows.filter((row) => row.length).map((row) => row.join(""));
  if (!words.length) return ["UNTITLED"];
  if (words.join(" ").length <= 15) return [words.join(" ")];

  let best = [words.join(" ")];
  let bestCost = Infinity;
  for (let split = 1; split < words.length; split += 1) {
    const candidate = [words.slice(0, split).join(" "), words.slice(split).join(" ")];
    const lengths = candidate.map((line) => line.length);
    const cost = Math.max(...lengths) * 2 + Math.abs(lengths[0] - lengths[1]);
    if (cost < bestCost) {
      best = candidate;
      bestCost = cost;
    }
  }
  return best;
}

const NEWSPAPER_COPY = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
  "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
  "Curabitur pretium tincidunt lacus. Nulla gravida orci a odio. Nullam varius, turpis et commodo pharetra, est eros bibendum elit, nec luctus magna felis sollicitudin mauris.",
  "Integer in mauris eu nibh euismod gravida. Duis ac tellus et risus vulputate vehicula. Donec lobortis risus a elit. Etiam tempor. Ut ullamcorper, ligula eu tempor congue, eros est euismod turpis.",
  "Nam dui mi, tincidunt quis, accumsan porttitor, facilisis luctus, metus. Phasellus ultrices nulla quis nibh. Quisque a lectus. Donec consectetuer ligula vulputate sem tristique cursus.",
  "Vivamus fermentum semper porta. Nunc diam velit, adipiscing ut tristique vitae, sagittis vel odio. Maecenas convallis ullamcorper ultricies. Curabitur ornare consequat nunc.",
  "Aliquam erat volutpat. Aenean placerat. In vulputate urna eu arcu. Aliquam erat volutpat. Suspendisse potenti. Morbi mattis felis at nunc. Duis viverra diam non justo.",
  "Fusce suscipit, wisi nec facilisis facilisis, est dui fermentum leo, quis tempor ligula erat quis odio. Nunc porta vulputate tellus. Praesent vitae arcu tempor neque lacinia pretium."
];

function newspaperColumns() {
  const columnXs = [70, 438, 806];
  return columnXs.map((x, columnIndex) => {
    const rotatedCopy = NEWSPAPER_COPY
      .map((_, index) => NEWSPAPER_COPY[(index + columnIndex * 3) % NEWSPAPER_COPY.length])
      .join(" ");
    const lines = wrapNewspaperCopy(rotatedCopy, 40).slice(0, 33);
    const lineMarkup = lines.map((line, lineIndex) => {
      const justification = line.length >= 30
        ? ' textLength="324" lengthAdjust="spacing"'
        : "";
      return `<text x="${x}" y="${525 + lineIndex * 23}" fill="#1b1718" font-family="IBM Plex Serif, Georgia, serif" font-size="17"${justification}>${escapeXml(line)}</text>`;
    }).join("");
    return `<g data-newspaper-column="${columnIndex + 1}">${lineMarkup}</g>`;
  }).join("");
}

function wrapNewspaperCopy(copy, maxLength) {
  const words = copy.split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length > maxLength && line) {
      lines.push(line);
      line = word;
      return;
    }
    line = candidate;
  });
  if (line) lines.push(line);
  return lines;
}

function ordinalSuffix(day) {
  if (day % 100 >= 11 && day % 100 <= 13) return "th";
  if (day % 10 === 1) return "st";
  if (day % 10 === 2) return "nd";
  if (day % 10 === 3) return "rd";
  return "th";
}

function round(value) {
  return Math.round(value * 10) / 10;
}

function escapeXml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}
